import MapElementProps from "../MapElementProps";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import {disconnect, getTiles, retainTilesWithin, selectTiles, Tile} from "../../../redux/features/firestore/tiles/slice";
import {colorToHex} from "../../../redux/features/color/color";
import {Position} from "geojson";
import GeoJsonLayer from "./GeoJsonLayer";
import {useEffect, useMemo, useRef} from "react";
import {MapBounds, MapState, selectMapState} from "../../../redux/features/map/slice";
import * as turf from '@turf/turf';
import {boundsContainsBounds, inflateBounds} from "../../../redux/features/map/tools";

// How far outside the current viewport before we re-query Firestore.
// Larger = fewer re-queries while panning, but stale tiles at edges.
const REFETCH_BUFFER_METERS = 2000;
// How much beyond the visible viewport to pre-fetch (scroll look-ahead).
// Keep this small — it's the main driver of response size.
const QUERY_EXTENSION_METERS = 500;
// Minimum ms between Firestore resubscriptions when the viewport moves.
const REFETCH_DEBOUNCE_MS = 2000;

const TilesLayer = ({map}: MapElementProps) => {
    const tiles: { [lng: number]: { [lat: number]: Tile } } = useAppSelector(selectTiles);
    const dispatch = useAppDispatch();
    const mapState: MapState = useAppSelector(selectMapState);

    const lastBounds = useRef<MapBounds>();
    const mapStateRef = useRef(mapState);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Keep ref in sync so the debounced callback always reads the latest mapState.
    mapStateRef.current = mapState;

    const fetchTiles = (state: MapState) => {
        // Always derive bounds from the live Mapbox instance — never from Redux
        // state, which only updates when location/zoom changes and can have stale
        // bounds (e.g. still the initial ±10° box) that produce a multi-thousand-km
        // radius and try to stream the entire Firestore collection.
        const mapBounds = map!.getBounds();
        const liveBounds: MapBounds = {
            north: mapBounds.getNorth(),
            south: mapBounds.getSouth(),
            east:  mapBounds.getEast(),
            west:  mapBounds.getWest(),
        };

        lastBounds.current = inflateBounds(liveBounds, REFETCH_BUFFER_METERS);

        // Evict tiles outside the new buffered viewport before loading the new
        // area, so the tile dict (and the GeoJSON built from it) stays bounded.
        dispatch(retainTilesWithin(lastBounds.current));

        const radiusToCorner = turf.distance(
            [state.locationAndZoom.location.longitude, state.locationAndZoom.location.latitude],
            [liveBounds.east, liveBounds.north],
            { units: 'meters' }
        );

        // Safety cap: never query more than 10 km from centre regardless of zoom level.
        const radiusInMeters = Math.min(radiusToCorner + QUERY_EXTENSION_METERS, 10000);

        dispatch(getTiles({
            coordinate: state.locationAndZoom.location,
            radiusInMeters,
        }));
    };

    // Load tiles whenever the viewport changes or on first real location.
    useEffect(() => {
        const { latitude, longitude } = mapState.locationAndZoom.location;
        // Skip the Redux default state (0,0) — wait for a real location.
        if (latitude === 0 && longitude === 0) return;

        // First real location: fetch immediately.
        if (!lastBounds.current) {
            fetchTiles(mapState);
            return;
        }

        // Viewport moved outside the buffered area: debounce to avoid
        // hammering Firestore while the user is panning.
        const mb = map!.getBounds();
        const currentBounds: MapBounds = {
            north: mb.getNorth(), south: mb.getSouth(),
            east: mb.getEast(), west: mb.getWest(),
        };
        if (!boundsContainsBounds(lastBounds.current, currentBounds)) {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                fetchTiles(mapStateRef.current);
                debounceTimer.current = null;
            }, REFETCH_DEBOUNCE_MS);
        }
    }, [mapState]); // eslint-disable-line

    const get_data = (): GeoJSON.FeatureCollection<GeoJSON.Geometry> => {
        const data: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
            type: "FeatureCollection",
            features: [],
        };

        // Group by color + glow flag so the glow halo layers can filter on
        // their own MultiPolygon features.
        const tilesPerGroup: { [key: string]: { color: string, glow: boolean, tiles: Tile[] } } = {};
        for (const tileLng in tiles) {
            for (const tileLat in tiles[tileLng]) {
                const tile: Tile = tiles[tileLng][tileLat];
                const hexColor = colorToHex(tile.color);
                const glow = tile.glow === true;
                const key = `${hexColor}:${glow ? '1' : '0'}`;
                if (!(key in tilesPerGroup)) {
                    tilesPerGroup[key] = { color: hexColor, glow, tiles: [] };
                }
                tilesPerGroup[key].tiles.push(tile);
            }
        }

        for (const key in tilesPerGroup) {
            const { color, glow, tiles: groupTiles } = tilesPerGroup[key];
            const coordinates: Position[][][] = [];
            for (const tile of groupTiles) {
                const tileBounds = tile.bounds;
                coordinates.push([[
                    [tileBounds.east, tileBounds.north],
                    [tileBounds.east, tileBounds.south],
                    [tileBounds.west, tileBounds.south],
                    [tileBounds.west, tileBounds.north],
                    [tileBounds.east, tileBounds.north],
                ]])
            }
            data.features.push({
                type: 'Feature',
                geometry: {
                    type: 'MultiPolygon',
                    coordinates: coordinates
                },
                properties: {
                    "stroke": color,
                    "fill": color,
                    "stroke-width": 0.1,
                    "glow": glow,
                },
            });
        }

        return data;
    };

    // Glow pulse: slow sinusoidal "breathing" of the halo layers' opacity,
    // matching the mobile app. Scalar inside the zoom step expression keeps
    // the zoom gating intact. 80ms ticks are smooth enough for a 2.5s wave.
    useEffect(() => {
        if (!map) return;
        const id = setInterval(() => {
            if (!map.getLayer('TilesLayerGlowOuter')) return;
            const pulse = 0.5 + 0.5 * Math.sin((Date.now() / 2500) * Math.PI * 2);
            map.setPaintProperty('TilesLayerGlowOuter', 'line-opacity',
                ['step', ['zoom'], 0, 14, 0.15 + 0.45 * pulse] as any);
            map.setPaintProperty('TilesLayerGlowInner', 'line-opacity',
                ['step', ['zoom'], 0, 14, 0.4 + 0.6 * pulse] as any);
        }, 80);
        return () => clearInterval(id);
    }, [map]);

    useEffect(() => {
        return () => {
            // Reset lastBounds so the next mount (e.g. React Strict Mode remount)
            // always triggers a fresh fetch instead of skipping because the ref
            // from the cancelled mount is still set.
            lastBounds.current = undefined;
            dispatch(disconnect());
        };
    }, []);

    // Rebuild the GeoJSON only when the tile set actually changes — not on every
    // render (moveend, loading toggles, etc.). An unmemoized rebuild produced a
    // new `data` object each render, which made every GeoJsonLayer re-ship the
    // full (growing) geometry to Mapbox on a 100ms loop and thrashed the tab.
    const data = useMemo(get_data, [tiles]); // eslint-disable-line
    return (
        <>
            {/* Glow halo: two stacked blurred lines under the fill — a wide soft
                outer bloom and a tighter brighter inner ring. Declared before the
                fill layer so they render underneath it. */}
            <GeoJsonLayer map={map} data={data} sourceId="tiles_source" layer={{
                id: "TilesLayerGlowOuter",
                type: 'line',
                layout: {},
                filter: ['==', ['get', 'glow'], true],
                paint: {
                    'line-color': ['get', 'fill'],
                    'line-opacity': ['step', ['zoom'], 0, 14, 0.6] as any,
                    'line-opacity-transition': { duration: 0, delay: 0 } as any,
                    'line-width': 16,
                    'line-blur': 14,
                }
            }}/>
            <GeoJsonLayer map={map} data={data} sourceId="tiles_source" layer={{
                id: "TilesLayerGlowInner",
                type: 'line',
                layout: {},
                filter: ['==', ['get', 'glow'], true],
                paint: {
                    'line-color': ['get', 'fill'],
                    'line-opacity': ['step', ['zoom'], 0, 14, 1] as any,
                    'line-opacity-transition': { duration: 0, delay: 0 } as any,
                    'line-width': 7,
                    'line-blur': 4,
                }
            }}/>
            {/* Fill layer: only visible at zoom >= 15 for performance.
                Both layers share one Mapbox source (tiles_source) to avoid
                loading the same GeoJSON twice into GPU memory. */}
            <GeoJsonLayer map={map} data={data} sourceId="tiles_source" layer={{
                id: "TilesLayerFill",
                type: 'fill',
                layout: {},
                paint: {
                    'fill-color': ['get', 'fill'],
                    // Below zoom 15: no fill (outline-only mode).
                    // Below zoom 12: tiles are effectively invisible.
                    'fill-opacity': [
                        'step', ['zoom'],
                        0,    // < 12: hidden
                        12, 0, // 12-14.9: outline-only (fill transparent)
                        15, 0.85, // >= 15: full fill
                    ] as any,
                }
            }}/>
            {/* Outline layer: visible between zoom 12 and 15 to show tile boundaries
                without the fill-rendering cost of thousands of individual polygons */}
            <GeoJsonLayer map={map} data={data} sourceId="tiles_source" layer={{
                id: "TilesLayerOutline",
                type: 'line',
                layout: {},
                paint: {
                    'line-color': ['get', 'fill'],
                    // Visible between 12 and 15; above 15 the fill layer takes over.
                    'line-opacity': [
                        'step', ['zoom'],
                        0,    // < 12: hidden
                        12, 0.5, // 12-14.9: outline visible
                        15, 0,   // >= 15: hide outlines (fills take over)
                    ] as any,
                    'line-width': 1,
                }
            }}/>
        </>
    );
}

export default TilesLayer;